import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    Autocomplete,
    Typography,
    Grid,
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useComponentType } from "../../../hooks/maintenance/useComponentType";
import { useSystemVehicleOptions } from "../../../hooks/maintenance/useSystemVehicleOptions";

// Validación
const componentSchema = yup.object().shape({
    tipcomid: yup
        .number()
        .typeError("Seleccione un tipo de componente válido.")
        .required("El tipo de componente es obligatorio.")
        .positive("Seleccione un tipo de componente válido."),
    // NUEVO: sistema vehicular requerido
    sisvehid: yup
        .number()
        .typeError("Seleccione un sistema vehicular válido.")
        .required("El sistema vehicular es obligatorio.")
        .positive("Seleccione un sistema vehicular válido."),
    comnom: yup.string().nullable().max(100, "El nombre no puede exceder 100 caracteres."),
    comdes: yup.string().nullable().max(255, "La descripción no puede exceder 255 caracteres."),
    comest: yup.string().required("El estado es obligatorio.").oneOf(['ACTIVO', 'MANTENIMIENTO'], "Estado inválido."),
    comfecins: yup
        .date()
        .nullable()
        .typeError("Formato de fecha inválido.")
        .max(new Date(), "La fecha de instalación no puede ser futura."),
    comkmins: yup
        .number()
        .nullable()
        .min(0, "El kilometraje no puede ser negativo.")
        .typeError("El kilometraje debe ser un número."),
});

const ComponentForm = ({ open, handleClose, onSubmit, defaultValues }) => {
    const { componentTypes, isLoading: isLoadingComponentTypes, error: errorComponentTypes } = useComponentType();   

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(componentSchema),
        defaultValues: {
            vehid: defaultValues.vehid,
            tipcomid: defaultValues.tipcomid || "",
            // NUEVO campo: sisvehid
            sisvehid: defaultValues.sisvehid ?? null,
            comnom: defaultValues.comnom || "",
            comdes: defaultValues.comdes || "",
            comest: defaultValues.comest || "ACTIVO",
            comfecins: defaultValues.comfecins ? new Date(defaultValues.comfecins) : null,
            comkmins: defaultValues.comkmins || null,
        },
    });

    const [selectedSystem, setSelectedSystem] = useState(null);
    const {
        options: systemOptions = [],
        isFetching: isFetchingSystems,
        search: systemSearch,
        setSearch: setSystemSearch,
    } = useSystemVehicleOptions('');

    useEffect(() => {
        if (!control._formValues.sisvehid) {
            setSelectedSystem(null);
            return;
        }
        const match = systemOptions.find(
            (o) => (o.sisvehid ?? o.sisvehID) === control._formValues.sisvehid
        );
        if (match) {
            setSelectedSystem(match);
        }
    }, [control._formValues.sisvehid, systemOptions]);

    // Reset al abrir/cambiar defaultValues (edición/creación)
    useEffect(() => {
        reset({
            vehid: defaultValues.vehid,
            tipcomid: defaultValues.tipcomid || "",
            sisvehid: defaultValues.sisvehid ?? null,
            comnom: defaultValues.comnom || "",
            comdes: defaultValues.comdes || "",
            comest: defaultValues.comest || "ACTIVO",
            comfecins: defaultValues.comfecins ? new Date(defaultValues.comfecins) : null,
            comkmins: defaultValues.comkmins || null,
        });
    }, [defaultValues, reset]);

    const handleFormSubmit = (data) => {
        const dataToSend = {
            ...data,
            vehid: defaultValues.vehid, // siempre obligamos vehid
            // seguridad: normaliza IDs a number
            tipcomid: Number(data.tipcomid),
            sisvehid: data.sisvehid != null ? Number(data.sisvehid) : null,
            comkmins: data.comkmins !== null && data.comkmins !== '' ? Number(data.comkmins) : null,
        };
        onSubmit(dataToSend);
    };

    const sisvehidWatch = useWatch({ control, name: 'sisvehid' });

    // cuando cambia el id o llegan nuevas options, fija la opción seleccionada
    useEffect(() => {
        if (sisvehidWatch == null || sisvehidWatch === '' || !Array.isArray(systemOptions)) {
            setSelectedSystem(null);
            return;
        }
        const idNum = Number(sisvehidWatch);
        const match = systemOptions.find?.(o => (o.sisvehid ?? o.sisvehID) === idNum);
        if (match) {
            setSelectedSystem(match);
        } else {
            // conserva la previa (evita parpadeo) o crea mínima estructura
            setSelectedSystem(prev => prev?.sisvehid === idNum || prev?.sisvehID === idNum ? prev : { sisvehid: idNum, sisvehnom: '' });
        }
    }, [sisvehidWatch, systemOptions]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                {defaultValues.comid ? "Editar Componente" : `Crear Componente para ${defaultValues.vehiculomatricula || 'Vehículo'}`}
            </DialogTitle>

            <DialogContent dividers>
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    {/* Vehículo (solo lectura) */}
                    <TextField
                        margin="dense"
                        label="Vehículo"
                        value={defaultValues.vehiculomatricula || defaultValues.vehid}
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{ mb: 2 }}
                    />

                    {/* Layout 2 columnas responsivo */}
                    <Grid container spacing={2}>
                        {/* Tipo de Componente */}
                        <Grid item xs={12} md={6}>
                            {isLoadingComponentTypes ? (
                                <Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /> <Typography variant="body2">Cargando tipos...</Typography></Box>
                            ) : errorComponentTypes ? (
                                <Typography color="error">Error al cargar tipos de componente: {errorComponentTypes.message}</Typography>
                            ) : (
                                <Controller
                                    name="tipcomid"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth error={!!errors.tipcomid}>
                                            <InputLabel id="tip-com-id-label">Tipo de Componente</InputLabel>
                                            <Select
                                                labelId="tip-com-id-label"
                                                label="Tipo de Componente"
                                                {...field}
                                                value={field.value || ""}
                                            >
                                                <MenuItem value=""><em>Seleccione...</em></MenuItem>
                                                {componentTypes.map((type) => (
                                                    <MenuItem key={type.tipcomid} value={type.tipcomid}>
                                                        {type.tipcomnom}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors.tipcomid && <FormHelperText>{errors.tipcomid.message}</FormHelperText>}
                                        </FormControl>
                                    )}
                                />
                            )}
                        </Grid>

                        {/* NUEVO: Sistema Vehicular (Autocomplete asíncrono) */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="sisvehid"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        loading={isFetchingSystems}
                                        options={systemOptions ?? []}
                                        value={selectedSystem}
                                        filterOptions={(x) => x ?? []}
                                        onChange={(_, option) => {
                                            const val = option ? Number(option.sisvehid ?? option.sisvehID) : null;
                                            setSelectedSystem(option || null);
                                            field.onChange(val); // guarda number|null en el form
                                        }}
                                        // actualiza búsqueda
                                        onInputChange={(_, value) => setSystemSearch(value || "")}
                                        getOptionLabel={(opt) =>
                                            opt?.sisvehnom
                                                ? `${opt.sisvehnom}${opt.sisvehdes ? " — " + opt.sisvehdes : ""}`
                                                : ""
                                        }
                                        isOptionEqualToValue={(opt, val) =>
                                            (opt.sisvehid ?? opt.sisvehID) === (val?.sisvehid ?? val?.sisvehID)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Sistema Vehicular"
                                                margin="dense"
                                                fullWidth
                                                error={!!errors.sisvehid}
                                                helperText={errors.sisvehid?.message}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {isFetchingSystems ? <CircularProgress size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Nombre */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="comnom"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="dense"
                                        label="Nombre del Componente (Opcional)"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        error={!!errors.comnom}
                                        helperText={errors.comnom?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Estado */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="comest"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth margin="dense" error={!!errors.comest}>
                                        <InputLabel id="com-est-label">Estado del Componente</InputLabel>
                                        <Select labelId="com-est-label" label="Estado del Componente" {...field}>
                                            <MenuItem value="ACTIVO">ACTIVO</MenuItem>
                                            <MenuItem value="MANTENIMIENTO">MANTENIMIENTO</MenuItem>
                                        </Select>
                                        {errors.comest && <FormHelperText>{errors.comest.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {/* Descripción */}
                        <Grid item xs={12}>
                            <Controller
                                name="comdes"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="dense"
                                        label="Descripción (Opcional)"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        error={!!errors.comdes}
                                        helperText={errors.comdes?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Fecha y Km */}
                        <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <Controller
                                    name="comfecins"
                                    control={control}
                                    render={({ field: { onChange, value, ...field } }) => (
                                        <DatePicker
                                            {...field}
                                            label="Fecha de Instalación (Opcional)"
                                            value={value}
                                            onChange={(newValue) => onChange(newValue)}
                                            inputFormat="dd/MM/yyyy"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    margin="dense"
                                                    fullWidth
                                                    error={!!errors.comfecins}
                                                    helperText={errors.comfecins?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="comkmins"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="dense"
                                        label="Kilometraje de Instalación (Opcional)"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        error={!!errors.comkmins}
                                        helperText={errors.comkmins?.message}
                                        inputProps={{ min: 0 }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="inherit">Cancelar</Button>
                <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" color="primary" disabled={isSubmitting}>
                    {defaultValues.comid ? "Guardar Cambios" : "Crear Componente"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ComponentForm;
